import { useMemo, useState } from "react";
import { Ship, TrendingUp, AlertTriangle } from "lucide-react";
import { useLang } from "../context/LanguageContext";

// Freight rates — per provider / port / container.
// weightCheck returns surcharge in USD for heavy cargo tiers.
const RATES = [
  { provider: "ONE",   port: "Shanghai",    type: "20GP", base: 2350, fees: 35,  weightCheck: (w) => (w >= 18 ? 600 : w >= 16 ? 300 : 0) },
  { provider: "ONE",   port: "Shanghai",    type: "40HQ", base: 3000, fees: 35,  weightCheck: () => 0 },
  { provider: "ABBAS", port: "Shanghai",    type: "20GP", base: 2500, fees: 52,  weightCheck: (w) => (w > 21 ? 50 : 0) },
  { provider: "ABBAS", port: "Shanghai",    type: "40HQ", base: 3500, fees: 104, weightCheck: () => 0 },
  { provider: "MSC",   port: "Shenzhen/GZ", type: "20GP", base: 2520, fees: 0,   weightCheck: (w) => (w >= 23 ? 200 : 0) },
  { provider: "MSC",   port: "Shenzhen/GZ", type: "40HQ", base: 3640, fees: 0,   weightCheck: () => 0 },
  { provider: "COSCO", port: "Tianjin",     type: "20GP", base: 3225, fees: 25,  weightCheck: () => 0 },
  { provider: "COSCO", port: "Tianjin",     type: "40HQ", base: 3750, fees: 25,  weightCheck: () => 0 },
  { provider: "Devin", port: "Shenzhen/GZ", type: "20GP", base: 2625, fees: 0,   weightCheck: () => 0 },
];

const PORT_OPTIONS = [
  { value: "Shanghai",    label: "Shanghai" },
  { value: "Shenzhen/GZ", label: "Shenzhen / Guangzhou" },
  { value: "Tianjin",     label: "Tianjin" },
];

const TYPE_OPTIONS = [
  { value: "20GP", label: "20GP (Standard)" },
  { value: "40HQ", label: "40HQ (High Cube)" },
];

export default function LogisticsCalculator() {
  const { t } = useLang();
  const c = t.logisticsPage.calculator;

  const [port, setPort] = useState("Shanghai");
  const [type, setType] = useState("20GP");
  const [weight, setWeight] = useState(15);
  // Service margin is applied silently (not shown to clients).
  const margin = "1.10";

  const result = useMemo(() => {
    const m = parseFloat(margin) || 1;
    const w = parseFloat(weight) || 0;

    const options = RATES
      .filter((r) => r.port === port && r.type === type)
      .map((r) => {
        const weightFee = r.weightCheck(w);
        const subtotal = r.base + r.fees + weightFee;
        return {
          ...r,
          weightFee,
          subtotal,
          total: subtotal * m,
          surchargeWithMargin: weightFee * m,
        };
      })
      .sort((a, b) => a.total - b.total);

    return {
      best: options[0] || null,
      allOptions: options,
    };
  }, [port, type, weight, margin]);

  const fmt = (n) =>
    "$" + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <section
      className="py-24 md:py-32 bg-white border-t border-[#E2E8F0]"
      data-testid="logistics-calculator-section"
    >
      <div className="pgb-container">
        <div className="max-w-3xl mb-12">
          <span className="eyebrow text-[#1D4ED8]">{c.eyebrow}</span>
          <h2 className="mt-4 text-3xl md:text-5xl font-black tracking-tighter text-[#0B1B29]">
            {c.title}
          </h2>
          <p className="mt-5 text-[#536878] leading-relaxed max-w-2xl">
            {c.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Inputs card */}
          <div
            className="lg:col-span-7 bg-[#F5F5F7] border border-[#E2E8F0] p-6 md:p-8"
            data-testid="calc-inputs"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label={c.fields.port} testId="calc-field-port">
                <select
                  value={port}
                  onChange={(e) => setPort(e.target.value)}
                  className="calc-input"
                  data-testid="calc-port"
                >
                  {PORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>

              <Field label={c.fields.type} testId="calc-field-type">
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="calc-input"
                  data-testid="calc-type"
                >
                  {TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>

              <Field label={c.fields.weight} testId="calc-field-weight">
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  className="calc-input"
                  data-testid="calc-weight"
                />
              </Field>
            </div>

            <p className="mt-6 text-xs text-[#536878] leading-relaxed">
              {c.disclaimer}
            </p>
          </div>

          {/* Result card */}
          <div
            className="lg:col-span-5 bg-[#0B1B29] text-white p-6 md:p-8 relative overflow-hidden"
            data-testid="calc-result"
          >
            <div
              aria-hidden="true"
              className="absolute -top-24 -right-24 w-[340px] h-[340px] bg-[#1D4ED8] blur-[120px] opacity-40"
            />

            {result.best ? (
              <div className="relative">
                <span className="eyebrow text-[#F59E0B]">{c.recommended}</span>

                <div className="mt-6 flex items-start justify-between gap-6">
                  <div className="flex-1 min-w-0">
                    <h3
                      className="text-3xl md:text-4xl font-black tracking-tighter"
                      data-testid="calc-best-provider"
                    >
                      {result.best.provider} {c.line}
                    </h3>
                    <p className="mt-2 text-sm text-[#93C5FD] flex items-center gap-2">
                      <Ship size={14} />
                      <span>
                        {result.best.port} → Poti · {c.onwardBaku} · {result.best.type}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-white/15">
                  <div
                    className="text-5xl md:text-6xl font-black tracking-tighter"
                    style={{ fontFamily: "Chivo" }}
                    data-testid="calc-best-total"
                  >
                    {fmt(result.best.total)}
                  </div>
                  <div className="mt-2 text-[10px] tracking-[0.22em] uppercase text-white/60">
                    {c.totalLabel}
                  </div>
                </div>

                {result.best.weightFee > 0 && (
                  <div
                    className="mt-5 flex items-start gap-2 text-[12px] text-[#F59E0B]/95 leading-relaxed"
                    data-testid="calc-weight-warning"
                  >
                    <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                    <span>
                      {c.weightSurcharge.replace(
                        "{amount}",
                        fmt(result.best.surchargeWithMargin)
                      )}
                    </span>
                  </div>
                )}

                {result.allOptions.length > 1 && (
                  <div className="mt-8 pt-5 border-t border-white/10">
                    <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-white/50 mb-3">
                      <TrendingUp size={12} />
                      <span>{c.otherQuotes}</span>
                    </div>
                    <ul className="space-y-2 text-sm" data-testid="calc-other-quotes">
                      {result.allOptions.slice(1).map((o) => (
                        <li
                          key={o.provider}
                          className="flex justify-between items-center font-mono text-xs text-white/75"
                        >
                          <span>{o.provider}</span>
                          <span>{fmt(o.total)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-white/70">{c.noResult}</p>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .calc-input {
          width: 100%;
          padding: 11px 12px;
          background: #ffffff;
          border: 1px solid #E2E8F0;
          font-size: 14px;
          color: #0B1B29;
          outline: none;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .calc-input:focus {
          border-color: #1D4ED8;
          box-shadow: 0 0 0 3px rgba(29,78,216,0.15);
        }
      `}</style>
    </section>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#536878]">
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
